import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import { Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import { FilesBlock } from '#components/document-viewer/files/files-block';
import { normalizeFilesConfig } from '#components/document-viewer/files/files-config';

import {
  NP_FILES_TAG,
  parseMarkdownFilesConfig,
  serializeMarkdownFilesBlock,
  serializeMarkdownFilesInnerConfig,
} from './markdown-files';

interface MarkdownFilesNodeOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

interface MarkdownFilesNodeAttrs {
  data: ReturnType<typeof normalizeFilesConfig>;
  rawConfig: string;
  parseError: string | null;
}

function normalizeFilesAttrs(attrs: unknown): MarkdownFilesNodeAttrs {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};
  return {
    data: normalizeFilesConfig(rec.data),
    rawConfig: typeof rec.rawConfig === 'string' ? rec.rawConfig : '',
    parseError: typeof rec.parseError === 'string' ? rec.parseError : null,
  };
}

function MarkdownFilesNodeView({ extension, selected, node }: NodeViewProps) {
  const { contextRef } = extension.options as MarkdownFilesNodeOptions;
  const context = contextRef.current;
  const attrs = normalizeFilesAttrs(node.attrs);

  return (
    <NodeViewWrapper
      className={
        selected
          ? 'markdown-files-node markdown-files-node--selected'
          : 'markdown-files-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <FilesBlock
        config={attrs.data}
        parseError={attrs.parseError}
        documentId={context.documentId}
        apiHost={context.apiHost}
        userEmail={context.userEmail}
        documentPath={context.parentPath}
      />
    </NodeViewWrapper>
  );
}

export const MarkdownFilesNode = Node.create<MarkdownFilesNodeOptions>({
  name: 'npFiles',
  group: 'block',
  atom: true,
  selectable: true,
  isolating: true,
  defining: true,

  addOptions() {
    return {
      contextRef: { current: null as never },
    };
  },

  addAttributes() {
    return {
      data: {
        default: normalizeFilesConfig({}),
      },
      rawConfig: {
        default: '',
      },
      parseError: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: NP_FILES_TAG,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false;

          const parsed = parseMarkdownFilesConfig(element.textContent ?? '');
          return {
            data: parsed.data,
            rawConfig: parsed.rawConfig,
            parseError: parsed.parseError,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = normalizeFilesAttrs(HTMLAttributes);
    return [NP_FILES_TAG, serializeMarkdownFilesInnerConfig(attrs)];
  },

  renderMarkdown(node) {
    const attrs = normalizeFilesAttrs(node?.attrs);
    return serializeMarkdownFilesBlock(attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MarkdownFilesNodeView);
  },
});
